import ServiceCourse from '@/service/ServiceCourse';
import { SERVICE_TYPE_DICT, REPLY_DICT } from '@/core/dict/service-type-dict';
import imageCompression from 'browser-image-compression';
import { TAXI_COURSE_RIDE_STATUS } from '@/core/dict/taxi-course-dict';
import download from 'downloadjs';
import * as types from './types/service-mutation-types';
import { ServiceMapper } from '../../../shared/mappers/service.mapper';
// TODO: think on strucutre of modals and data flow of modals for selecting the service item and submitting the form
// TODO: think on how to strucutre data to access whether the service item was added or not

async function compressFiles(files) {
  return Promise.all(
    files.map(async (file) => {
      if (file.type && file.type.startsWith('image/')) {
        try {
          const compressed = await imageCompression(file, {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
          return compressed;
        } catch (error) {
          console.error('Error compressing image:', error);
          return file;
        }
      }

      return file;
    })
  );
}

class Service {
  constructor() {
    this.company = null;
    this.services = [];
  }
}

export default {
  namespaced: true,
  state: {
    // ? used for creation a new service
    service: new Service(),
    // ? used for serwis-kurs a.k.a serwis-info
    serviceCourse: null,
  },
  getters: {
    service: (state) => state.service,
    serviceCourse: (state) => state.serviceCourse,
    serviceCompany: (state) => state.service.company,
    services: (state) => state.service.services,
    isSupplementingConsumablesAdded: (state) =>
      state.service.services.some(({ serviceType }) => serviceType === SERVICE_TYPE_DICT.SUPPLEMENTING_MATERIALS),
    isFleetServiceAdded: (state) =>
      state.service.services.some(({ serviceType }) => serviceType === SERVICE_TYPE_DICT.FLEET_SERVICE),
    isLocomotiveWashingAdded: (state) =>
      state.service.services.some(({ serviceType }) => serviceType === SERVICE_TYPE_DICT.LOCOMOTIVE_WASHING),
    isLocomotiveReviewP1Added: (state) =>
      state.service.services.some(({ serviceType }) => serviceType === SERVICE_TYPE_DICT.REVIEW_P1),
    isLocomotiveReviewP2Added: (state) =>
      state.service.services.some(({ serviceType }) => serviceType === SERVICE_TYPE_DICT.REVIEW_P2),
    isLocomotiveReviewP3Added: (state) =>
      state.service.services.some(({ serviceType }) => serviceType === SERVICE_TYPE_DICT.REVIEW_P3),
    isTrainCleaningAdded: (state) =>
      state.service.services.some(({ serviceType }) => serviceType === SERVICE_TYPE_DICT.TRAIN_CLEANING),
    isCargoCorrectionAdded: (state) =>
      state.service.services.some(({ serviceType }) => serviceType === SERVICE_TYPE_DICT.CARGO_CORRECTION),
    isServiceItemsAdded: (state) => state.service.services.length > 0,
  },
  mutations: {
    [types.SET_COMPANY]: (state, val) => {
      state.service.company = val;
    },
    [types.ADD_SERVICE_ITEM]: (state, val) => {
      state.service.services.push(val);
    },
    [types.REMOVE_SERVICE_ITEM]: (state, val) => {
      state.service.services = state.service.services.filter((service) => service.serviceType !== val);
    },
    [types.UPDATE_SERVICE_ITEM]: (state, service) => {
      const index = state.service.services.findIndex((s) => s.serviceType === service.serviceType);
      if (index !== -1) {
        state.service.services.splice(index, 1, service);
      }
    },
    [types.RESET_STATE]: (state) => {
      state.service = new Service();
    },
    [types.SET_SERVICE_COURSE]: (state, val) => {
      state.serviceCourse = val;
    },
  },
  actions: {
    setCompany({ commit }, val) {
      commit(types.SET_COMPANY, val);
    },
    addService({ commit }, val) {
      if ('attachments' in val && val.attachments.length > 0) {
        commit('setLoading', true, { root: true });
        Promise.all(val.attachments.map((file) => ServiceCourse.addFile(file)))
          .then((files) => {
            commit(types.ADD_SERVICE_ITEM, {
              ...val,
              attachments: files.map((file) => ({ ...file, name: file.filePath })),
            });
          })
          .finally(() => {
            commit('setLoading', false, { root: true });
          });
      } else {
        commit(types.ADD_SERVICE_ITEM, val);
      }
    },
    updateService({ commit }, service) {
      if ('attachments' in service && service.attachments.length > 0) {
        commit('setLoading', true, { root: true });
        const isMediaObject = (file) => file && 'id' in file;
        Promise.all(
          service.attachments.filter((file) => !isMediaObject(file)).map((file) => ServiceCourse.addFile(file))
        )
          .then((files) => {
            commit(types.UPDATE_SERVICE_ITEM, {
              ...service,
              attachments: [...service.attachments.filter((file) => isMediaObject(file)), ...files].map((file) => ({
                ...file,
                name: file.filePath,
              })),
            });
          })
          .finally(() => {
            commit('setLoading', false, { root: true });
          });
      }
    },
    removeService({ commit }, serviceType) {
      commit(types.REMOVE_SERVICE_ITEM, serviceType);
    },
    createService({ commit, getters }, payload) {
      commit('setLoading', true, { root: true });
      return ServiceCourse.createService({
        baseForm: payload,
        service: getters.service,
      }).finally(() => {
        commit('setLoading', false, { root: true });
      });
    },
    resetState({ commit }) {
      commit(types.RESET_STATE);
    },
    removeServiceItemFromExistingServiceCourse({ commit, dispatch }, { course, ride, serviceType }) {
      commit('setLoading', true, { root: true });
      return ServiceCourse.removeServiceItem({
        serviceRideId: ride.id,
        services: ride.services.filter((v) => v.serviceType !== serviceType).map((service) => service['@id']),
      })
        .then(async () => {
          await dispatch(
            'taxiCourse/updateServiceCourse',
            {
              ...course,
              rides: course.rides.map((r) => {
                if (r.id === ride.id) {
                  return {
                    ...r,
                    services: r.services.filter((v) => v.serviceType !== serviceType),
                  };
                }
                return r;
              }),
            },
            { root: true }
          );
        })
        .finally(() => {
          commit('setLoading', false, { root: true });
        });
    },
    async updateServiceItemFromExistingServiceCourse({ commit, dispatch }, { course, ride, service }) {
      commit('setLoading', true, { root: true });
      const isMediaObject = (file) => file && 'id' in file;
      let attachments = [];
      if ('attachments' in service && service.attachments.length > 0) {
        attachments = await Promise.all(
          service.attachments.filter((file) => !isMediaObject(file)).map((file) => ServiceCourse.addFile(file))
        );
      }
      return ServiceCourse.updateServiceItem({
        serviceRideId: ride.id,
        services: ride.services.map((v) =>
          v.serviceType === service.serviceType
            ? {
                id: v['@id'],
                ...ServiceMapper.serviceToDTO({
                  ...service,
                  attachments: [
                    ...(service.attachments ?? [])
                      .filter((file) => isMediaObject(file))
                      .map((file) => file.mediaObject),
                    ...attachments,
                  ],
                }),
              }
            : v['@id']
        ),
      })
        .then(async (serviceCourse) => {
          await dispatch(
            'taxiCourse/updateServiceCourse',
            {
              ...course,
              rides: course.rides.map((r) => {
                if (r.id === ride.id) {
                  return {
                    ...r,
                    services: serviceCourse.services.map((v) =>
                      v.serviceType === service.serviceType
                        ? {
                            ...v,
                            ...{
                              ...service,
                              attachments: v.attachments.map((file) => ({
                                ...file,
                                name: file.mediaObject.filePath,
                              })),
                            },
                          }
                        : v
                    ),
                  };
                }
                return r;
              }),
            },
            { root: true }
          );
        })
        .finally(() => {
          commit('setLoading', false, { root: true });
        });
    },
    async addServiceItemFromExistingServiceCourse({ commit, dispatch }, { course, ride, service }) {
      commit('setLoading', true, { root: true });
      let attachments = [];
      if ('attachments' in service && service.attachments.length > 0) {
        attachments = await Promise.all(service.attachments.map((file) => ServiceCourse.addFile(file)));
      }

      return ServiceCourse.addServiceItem({
        serviceRideId: ride.id,
        services: ride.services.map((v) => v['@id']).concat(ServiceMapper.serviceToDTO({ ...service, attachments })),
      })
        .then(async (services) => {
          await dispatch(
            'taxiCourse/updateServiceCourse',
            {
              ...course,
              rides: course.rides.map((r) => {
                if (r.id === ride.id) {
                  const existingServices = r.services.map((v) => v['@id']);
                  const [addedService] = services.filter((v) => !existingServices.includes(v['@id']));
                  return {
                    ...r,
                    services: [...r.services, addedService],
                  };
                }
                return r;
              }),
            },
            { root: true }
          );
        })
        .finally(() => {
          commit('setLoading', false, { root: true });
        });
    },
    fetchServiceInfoByToken({ commit }, token) {
      commit('setLoading', true, { root: true });
      return ServiceCourse.fetchServiceInfoByToken(token)
        .then((response) => {
          commit(types.SET_SERVICE_COURSE, response);
        })
        .finally(() => {
          commit('setLoading', false, { root: true });
        });
    },
    acceptRide({ commit }, payload) {
      commit('setLoading', true, { root: true });
      return ServiceCourse.replyToRide({ ...payload, reply: REPLY_DICT.ACCEPT })
        .then((updatedCourse) => {
          commit(types.SET_SERVICE_COURSE, updatedCourse);
        })
        .catch((error) => error)
        .finally(() => {
          commit('setLoading', false, { root: true });
        });
    },
    declineRide({ commit }, payload) {
      commit('setLoading', true, { root: true });
      return ServiceCourse.replyToRide({ ...payload, reply: REPLY_DICT.DECLINE })
        .then((updatedCourse) => {
          commit(types.SET_SERVICE_COURSE, updatedCourse);
        })
        .catch((error) => error)
        .finally(() => {
          commit('setLoading', false, { root: true });
        });
    },
    async saveServiceReport({ commit, getters }, { signedDocuments, documentsOfWork, ...payload }) {
      const rideId = getters.serviceCourse.rides[0].id;
      const isMediaObject = (file) => file && 'id' in file;
      commit('setLoading', true, { root: true });

      const compressedSignedDocs = await compressFiles(signedDocuments.filter((file) => !isMediaObject(file)));
      const compressedWorkDocs = await compressFiles(documentsOfWork.filter((file) => !isMediaObject(file)));

      const signedDocumentsMediaObjects = await Promise.all(
        compressedSignedDocs.map((file) => ServiceCourse.addFile(file))
      );
      const documentsOfWorkMediaObjects = await Promise.all(
        compressedWorkDocs.map((file) => ServiceCourse.addFile(file))
      );

      Promise.all([
        ServiceCourse.addServiceReportAttachments({
          signedDocumentsMediaObjects: [
            ...signedDocumentsMediaObjects,
            ...signedDocuments
              .filter((file) => isMediaObject(file))
              .map(({ mediaObject }) => ({ '@id': mediaObject['@id'] })),
          ],
          documentsOfWorkMediaObjects: [
            ...documentsOfWorkMediaObjects,
            ...documentsOfWork
              .filter((file) => isMediaObject(file))
              .map(({ mediaObject }) => ({ '@id': mediaObject['@id'] })),
          ],
          rideId,
        }),
        ServiceCourse.saveServiceReport(payload),
      ])
        .then(([updatedRide, updatedCourse]) => {
          commit(types.SET_SERVICE_COURSE, {
            ...updatedCourse,
            rides: [{ ...updatedRide, status: TAXI_COURSE_RIDE_STATUS.FINISHED }],
          });
        })
        .finally(() => {
          commit('setLoading', false, { root: true });
        });
    },
    downloadAttachment({ getters }, { attachment, courseId }) {
      return ServiceCourse.downloadAttachment({
        courseId: getters.serviceCourse?.id || courseId,
        attachmentId: attachment.id,
      }).then(({ data }) => {
        download(data, attachment.name);
      });
    },
    updateServiceRelation({ getters, commit }, payload) {
      commit('setLoading', true, { root: true });
      return ServiceCourse.updateServiceRelation({ serviceRideId: getters.serviceCourse.rides[0].id, payload })
        .then((data) => {
          commit(types.SET_SERVICE_COURSE, {
            ...getters.serviceCourse,
            rides: getters.serviceCourse.rides.map((ride) => {
              if (ride.id === data.id) {
                return {
                  ...ride,
                  ...data,
                };
              }
              return ride;
            }),
          });
        })
        .finally(() => {
          commit('setLoading', false, { root: true });
        });
    },
  },
};
